import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Papa from 'papaparse';
import { InfoIcon } from './icons';
import algoliasearch from 'algoliasearch/lite';
import { getFromStorage, setInStorage } from '../utils/storage.js';
import { set } from 'lodash';


const SALESFORCE_MIDDLEWARE_HOST = 'https://48l0o3fcab.execute-api.ap-southeast-2.amazonaws.com/prod';

const locationRegex = /^([^,]*),\s*(.+?)(?=\s*([a-z]{2,3})$)/im;
const eventBus = require('js-event-bus')();




function Consultant({ view, showFindConsultant, showCheckout, openAssignConsultant, cancelPromptWhenAssigning = true, setOpenAssignConsultant }) {
  const defaultAssigningConsultant = openAssignConsultant ? true : false;

  const [consultantList, setConsultantList] = useState([]);
  const [consultant, setConsultant] = useState(null);
  const [consultantRequested, setConsultantRequested] = useState(false);
  const [assigningConsultant, setAssigningConsultant] = useState(defaultAssigningConsultant);
  const [algoliaIndex, setAlgoliaIndex] = useState(null);
  const [algoliaIndexCountry, setAlgoliaIndexCountry] = useState("Australia");  
  const [cancelPromptWhenAssigningState, setCancelPromptWhenAssigningState] = useState(cancelPromptWhenAssigning);

  useEffect(() => {
    /**
     * If a custom csv has been uploaded we will use this as our source for consultants
     */
    if (window.theme.consultants.csv) {

      Papa.parse(window.theme.consultants.csv, {
        download: true,
        header: true,
        complete: results => {
          /**
           * Add postcode, city and state data to the returned consultants
           * Logic here is the same as from the original theme
           */
          const data = results.data.map(consultant => {
            const locationData = consultant.location.match(locationRegex);
            return {
              ...consultant,
              city: locationData[2],
              state: locationData[3],
              postcode: locationData[1],
            };
          });
          setConsultantList(data);
        },
      });
    }
    if (window.theme.algolia) {
      let apiId = window.theme.algolia.appid
      let apiKey = window.theme.algolia.apikey
      let apiIndex = window.theme.algolia.consultant_index
      let country = window.theme.algolia.consultant_country
      if (!apiId || !apiKey || !apiIndex || !country) {
        console.error("Algolia Error: Missing id, key, or index. Check theme settings.")
      }
      const client = algoliasearch(apiId, apiKey);
      const index = client.initIndex(apiIndex);
      setAlgoliaIndex(index);
      setAlgoliaIndexCountry(country)
    }
  }, []);

  useEffect(() => {
    if (!algoliaIndex) return;
    /**
         * Check if a consultant ID exists in a `ref` attribute, as a cart attribute
         * or in local storage. If so load that consultant and store the value.
         */
    const tagConsultantID = window.theme?.consultants?.consultantId;
    const localStorageConsultantID = localStorage.getItem('consultant_id');
    const cartConsultantID = window.theme?.cart?.attributes?.consultant_id;
    const searchParams = new URLSearchParams(window.location.search);
    const refConsultantID = searchParams.get('ref');
    const consultantID = refConsultantID || cartConsultantID || localStorageConsultantID || tagConsultantID;

    if (consultantID) {
      algoliaIndex.search(consultantID, { filters: `Country:"${algoliaIndexCountry}"` }).then(({ hits }) => {
        // console.log("Consultant ID:", consultantID, hits);
        if (hits.length) {
          let result = hits.filter(hit => {
            if (hit.Contact_Netsuite_Internal_Id__c.toString() === consultantID.toString()) {
              // console.log("RESULT FOUND")
              return true;
            }
          })[0];
          let location = `${result.PostalCode}, ${result.City} ${result.State}`
          let profileImage = result.ProfileImage || null
          let consultant = {
            id: result.Contact_Netsuite_Internal_Id__c,
            consultant_id: result.Contact_Netsuite_Internal_Id__c,
            active: result.IsActive,
            algoliaId: result.Id,
            name: result.Name,
            city: result.City,
            state: result.State,
            postcode: result.PostalCode,
            location: location,
            photo: profileImage
          }
          setConsultant(consultant)
          localStorage.setItem('consultant_id', consultantID);
          localStorage.setItem('consultant_all', JSON.stringify(consultant));
          setAssigningConsultant(false);
          if (cartConsultantID && (!refConsultantID || refConsultantID === cartConsultantID)) return;

          handleUpdateCartAttributes(
            {
              consultant_id: consultant.id,
              consultant_name: consultant.name,
              consultant_location: consultant.location,
            },
            () => {
              if (window.theme.consultants && window.theme.consultants.id) {
                updateCustomer(window.theme.consultants.id, consultant.id);
              }
            }
          );
        }
        return;
      })
    };
  }, [algoliaIndex]);

  useEffect(() => {
    setAssigningConsultant(openAssignConsultant);
  }, [openAssignConsultant]);

  useEffect(() => {
    eventBus.on('eventUpdateConsultant', function() {
      const localStorageConsultant = localStorage.getItem('consultant_all') ? JSON.parse(localStorage.getItem('consultant_all')) : consultant;
      setConsultant(localStorageConsultant)
    });
  }, []);

  const handleAssigningConsultant = (item) => {
    setAssigningConsultant(item)
    setCancelPromptWhenAssigningState(item)
  }

  function updateCustomer(customerID, consultantID) {
    const endpoint = `${SALESFORCE_MIDDLEWARE_HOST}/consultants/${consultantID}/attach?shop=${window.Shopify.shop}`;
    axios({
      method: 'POST',
      url: endpoint,
      responseType: 'JSON',
      data: {
        data: {
          shopify_customer_id: customerID,
        },
      },
    }).catch(err => window.console.log(err));
  }

  function handleUpdateCartAttributes(attributes, onSuccess) {
    const rootUrl = window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '';
    axios({
      method: 'POST',
      url: `${rootUrl}/cart/update.js`,
      responseType: 'JSON',
      data: {
        attributes,
      },
    })
      .then(onSuccess)
      .catch(err => window.console.log(err));
  }

  if (assigningConsultant && (cancelPromptWhenAssigningState || consultant)) {
    try {
      const result = setInStorage('promptToAddConsultant', 'session');
    } catch (error) {
      console.error('Error assigning consultant to sessionStorage: ', error);
      return <></>;
    }

    return (
      <AssignConsultant
        algoliaIndex={algoliaIndex}
        algoliaIndexCountry={algoliaIndexCountry}
        showFindConsultant={showFindConsultant}
        showCheckout={showCheckout}
        consultantList={consultantList}
        setAssigningConsultant={setAssigningConsultant}
        consultantRequested={consultantRequested}
        setConsultant={setConsultant}
        setConsultantRequested={setConsultantRequested}
        updateCustomer={updateCustomer}
        handleUpdateCartAttributes={handleUpdateCartAttributes}
        setOpenAssignConsultant={setOpenAssignConsultant}
      />
    );
  } 

  if (consultant) {
    try {
      const promptToAddConsultant = setInStorage('promptToAddConsultant', false, 'session');
    } catch (error) {
      console.error('Error assigning consultant: ', error);
      return <></>;
    }
  
    let localStorageConsultant = consultant;
  
    try {
      const allConsultantsJSON = getFromStorage('consultant_all', 'local');
      const allConsultants = JSON.parse(allConsultantsJSON);
      if (allConsultants) {
        localStorageConsultant = allConsultants;
      }
    } catch (error) {
      console.error('Error getting consultants from localStorage: ', error);
      return <></>;
    }

    return (
      <YourConsultant
        view={view}
        {...localStorageConsultant}
        setConsultant={setConsultant}
        setAssigningConsultant={setAssigningConsultant}
      />
    );
  }

  return <NoConsultant view={view} setAssigningConsultant={handleAssigningConsultant} />;
}

function YourConsultant({ view, name, photo, city, state, postcode, setConsultant, setAssigningConsultant }) {
  if (view === 'account') {
    return (
      <div className="account__your-consultant">
        <p>{window.theme.strings.consultant.customerConsultantYourConsultant}</p>
        <p dangerouslySetInnerHTML={{ __html: window.theme.strings.consultant.customerConsultantLearnMore }} />
        <div className="your-consultant your-consultant--account">
          <div className="your-consultant__card">
            <div className="your-consultant__image" style={{ backgroundImage: `url("${photo}")` }}></div>
            <div className="your-consultant__details">
              <span className="your-consultant__name">{name}</span>
              <span className="your-consultant__location">
                {city}, {state} {postcode}
              </span>
            </div>
          </div>
          <div className="your-consultant__ctas">
            <button
              type="button"
              className="btn btn--primary btn--outline btn--small"
              onClick={() => {
                setConsultant(null);
                localStorage.removeItem('consultant_id');
              }}
            >
              Remove
            </button>
          </div>
        </div>
        <p>
          <a
            href="#"
            onClick={() => {
              setConsultant(null);
              localStorage.removeItem('consultant_id');
            }}
          >
            {window.theme.strings.consultant.customerConsultantNeedANewConsultant}
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="your-consultant" onClick={() => setAssigningConsultant(true)}>
      <p className="your-consultant__heading">{window.theme.strings.consultant.yourConsultant}</p>
      <div className="your-consultant__card">
        <div className="your-consultant__image" style={{ backgroundImage: `url("${photo}")` }}></div>
        <div className="your-consultant__details">
          <span className="your-consultant__name">{name}</span>
          <span className="your-consultant__location">
            {city}, {state} {postcode}
          </span>
        </div>
        <div className="your-consultant__ctas">
          <svg
            className="icon icon-pen"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.667 2.872C11.667 3.362 11.476 3.823 11.13 4.168L4.77 10.528C4.68977 10.6084 4.59027 10.6669 4.481 10.698L1.185 11.64C1.07067 11.6732 0.94952 11.6752 0.834176 11.6456C0.718832 11.6161 0.613507 11.5563 0.529166 11.4722C0.444826 11.3882 0.384554 11.2831 0.354626 11.1678C0.324698 11.0526 0.326209 10.9315 0.359 10.817L1.301 7.519C1.331 7.41 1.391 7.311 1.471 7.231L7.831 0.869C8.17487 0.525568 8.641 0.332667 9.127 0.332667C9.613 0.332667 10.0791 0.525568 10.423 0.869L11.13 1.576C11.476 1.922 11.667 2.383 11.667 2.872V2.872ZM3.879 9.593L8.862 4.61L7.39 3.138L2.406 8.121L1.818 10.181L3.878 9.593H3.879ZM10.333 2.781C10.3333 2.72188 10.3217 2.66329 10.2991 2.60868C10.2764 2.55408 10.2431 2.50455 10.201 2.463L9.537 1.798C9.45233 1.71391 9.33783 1.66672 9.2185 1.66672C9.09917 1.66672 8.98467 1.71391 8.9 1.798L8.471 2.228L9.772 3.528L10.201 3.1C10.243 3.05817 10.2762 3.00844 10.2989 2.95369C10.3215 2.89894 10.3331 2.84025 10.333 2.781V2.781Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

function NoConsultant({ view, setAssigningConsultant }) {
  if (view === 'account') {
    return (
      <div className="account__info-box text-center">
        <div>
          <InfoIcon />
          <h3 className="text-serif">{window.theme.strings.consultant.customerConsultantNone2}</h3>
          <p className="p-b-lg">{window.theme.strings.consultant.customerConsultantText}</p>
          <button type="button" className="btn" onClick={() => setAssigningConsultant(true)}>
            {window.theme.strings.consultant.customerConsultantFind2}
          </button>
          <p dangerouslySetInnerHTML={{ __html: window.theme.strings.consultant.customerConsultantLearnMore }} />
        </div>
      </div>
    );
  }

  return (
    <p className="no-consultant">
      <InfoIcon />
      {window.theme.strings.consultant.noConsultant}{' '}
      <a onClick={() => setAssigningConsultant(true)}>
        {window.theme.strings.consultant.addNow}
      </a>
    </p>
  );
}

function AssignConsultant({
  algoliaIndex,
  algoliaIndexCountry,
  showFindConsultant,
  showCheckout,
  consultantList,
  setAssigningConsultant,
  consultantRequested,
  setConsultant,
  setConsultantRequested,
  updateCustomer,
  handleUpdateCartAttributes,
  setOpenAssignConsultant
}) {
  const rootUrl = window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '';

  const defaultActiveTab = showFindConsultant ? null : 1;

  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [consultantName, setConsultantName] = useState('');
  const [matchingConsultantList, setMatchingConsultantList] = useState([]);

  function handleConsultantSearch(value) {
    if (value.length < 1) {
      setMatchingConsultantList([]);
      return;
    }
    algoliaIndex.search(value, { filters: `Country:"${algoliaIndexCountry}"` })
    .then(({ hits }) => {
      // console.log("UNFILTERED HITS:", hits);
      let consultantsSearch = hits.map(result => {
        let location = `${result.PostalCode}, ${result.City} ${result.State}`
        let profileImage = result.ProfileImage || null
        return {
          id: result.Contact_Netsuite_Internal_Id__c,
          consultant_id: result.Contact_Netsuite_Internal_Id__c,
          active: result.IsActive,
          algoliaId: result.Id,
          name: result.Name,
          city: result.City,
          state: result.State,
          postcode: result.PostalCode,
          location: location,
          photo: profileImage
        }
      })
      // console.log("FILTERED RESULTS:", consultantsSearch);
      setMatchingConsultantList(consultantsSearch);
    }).error(err => {
      console.error("Algolia Error: ", err)
      setMatchingConsultantList([]);
    })
  }

  const handleConsultantSelection = (selectedConsultant) => {
    handleUpdateCartAttributes(
      {
        consultant_id: selectedConsultant.id,
        consultant_name: selectedConsultant.name,
        consultant_location: selectedConsultant.location,
      },
      () => {
        if (window.theme.consultants && window.theme.consultants.id) {
          updateCustomer(window.theme.consultants.id, selectedConsultant.id);
        }
        setConsultant(selectedConsultant);
        setInStorage('consultant_id', selectedConsultant.id, 'local')
        setInStorage('consultant_all', JSON.stringify(selectedConsultant), 'local')
        setAssigningConsultant(false);
        eventBus.emit('eventUpdateConsultant');
      }
    );
  };
  return (
    <>
      <div className="assign-consultant">
        <div className="assign-consultant__header">
          <svg
            className="icon icon-consultant"
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.05005 10.0637H16.05"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.0537 8.568C16.8685 8.42792 17.5543 7.87925 17.8698 7.11504C18.1853 6.35082 18.0863 5.47812 17.6077 4.804C16.893 3.81987 15.5735 3.49337 14.4824 4.03067C14.2441 2.87732 13.2284 2.05001 12.0507 2.05001C10.873 2.05001 9.8573 2.87732 9.61904 4.03067C8.52469 3.49182 7.2012 3.82213 6.48837 4.812C6.01327 5.48715 5.91708 6.35859 6.2335 7.1211C6.54991 7.88361 7.23484 8.43091 8.04837 8.57131"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.05 8.568V11.55C16.05 13.7591 14.2592 15.55 12.05 15.55V15.55C9.84091 15.55 8.05005 13.7591 8.05005 11.55V8.5713"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.14 14.95L14.89 17.94"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9.21008 17.94L9.95008 14.95"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3.05005 21.55V21.1797C3.05005 19.7494 4.05959 18.5181 5.46205 18.2377L8.30805 17.6687C8.5432 17.6216 8.78741 17.6698 8.98705 17.8027V17.8027C10.8417 19.0397 13.2584 19.0397 15.113 17.8027V17.8027C15.3127 17.6698 15.5569 17.6216 15.792 17.6687L18.638 18.2377C20.0405 18.5181 21.0501 19.7494 21.05 21.1796V21.55"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h4 className="assign-consultant__heading">{window.theme.strings.consultant.assignConsultant}</h4>
          <p className="assign-consultant__description">
            {window.theme.strings.consultant.assignConsultantDescription}
          </p>
        </div>
        <div className="assign-consultant__content">
          <AssignConsultantTab
            id={1}
            title={window.theme.strings.consultant.haveConsultant}
            isActive={activeTab === 1}
            setActiveTab={setActiveTab}
          >
            <AssignConsultantTabContent>
              <p>{window.theme.strings.consultant.haveConsultantDescription}</p>
              <div className="assign-consultant__input">
                <input
                  type="text"
                  value={consultantName}
                  onChange={e => {
                    setConsultantName(e.target.value);
                    handleConsultantSearch(e.target.value);
                  }}
                  placeholder={window.theme.strings.consultant.haveConsultantPlaceholder}
                />
                <div className="assign-consultant__dropdown">
                  {matchingConsultantList.map(consultant => {
                    return (
                      <div
                        key={consultant.id}
                        className="assign-consultant__dropdown-item"
                        onClick={() => handleConsultantSelection(consultant)}
                      >
                        <span
                          className="assign-consultant__dropdown-image"
                          style={{ backgroundImage: `url("${consultant.photo}")` }}
                        />
                        <span className="assign-consultant__dropdown-name">
                          {consultant.name}, {consultant.city}, {consultant.state} {consultant.postcode}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </AssignConsultantTabContent>
          </AssignConsultantTab>
          {showFindConsultant && (
            <AssignConsultantTab
              id={2}
              title={window.theme.strings.consultant.findConsultant}
              isActive={activeTab === 2}
              setActiveTab={setActiveTab}
            >
              <AssignConsultantTabContent>
                {consultantRequested ? (
                  <button type="button" className="btn btn--primary btn--small" disabled>
                    {window.theme.strings.consultant.findConsultantConfirmation}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn--primary btn--small"
                    onClick={() =>
                      handleUpdateCartAttributes(
                        {
                          requested_consultant: true,
                        },
                        () => setConsultantRequested(true)
                      )
                    }
                  >
                    {window.theme.strings.consultant.findConsultantButton}
                  </button>
                )}
              </AssignConsultantTabContent>
            </AssignConsultantTab>
          )}
          {showCheckout && (
              <form action={'/cart'} method={'post'} >
                <button type={'submit'} name={'checkout'} className="assign-consultant__btn btn btn--outline btn--block">
                  {window.theme.strings.checkout}
                </button>
              </form>
          )}
        </div>
      </div>
      <button
        type="button"
        className="side-cart__close"
        aria-label={window.theme.strings.closeCart}
        onClick={() => {
          const newConsultantState = false;
          setAssigningConsultant(newConsultantState);
          setOpenAssignConsultant && setOpenAssignConsultant(false);
          sessionStorage.setItem('promptToAddConsultant', 'false');
        }}        
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
          <g fill="none" fillRule="evenodd" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 4l12 12M16 4L4 16" />
          </g>
        </svg>
      </button>
    </>
  );
}

function AssignConsultantTab({ id, title, isActive, setActiveTab, children }) {
  const tabClass = isActive ? 'assign-consultant__tab active' : 'assign-consultant__tab';
  return (
    <div className={tabClass}>
      <button type="button" className="assign-consultant__tab-toggle" onClick={() => setActiveTab(id)}>
        {title}
      </button>
      {isActive && children}
    </div>
  );
}

function AssignConsultantTabContent({ children }) {
  return <div className="assign-consultant__tab-content">{children}</div>;
}

Consultant.propTypes = {
  view: PropTypes.string,
  showFindConsultant: PropTypes.bool,
  showCheckout: PropTypes.bool,
  openAssignConsultant: PropTypes.bool,
  cancelPromptWhenAssigning: PropTypes.bool,
};

YourConsultant.propTypes = {
  view: PropTypes.string,
  name: PropTypes.string,
  photo: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  postcode: PropTypes.string,
  setConsultant: PropTypes.func,
  setAssigningConsultant: PropTypes.func,
};

NoConsultant.propTypes = {
  view: PropTypes.string,
  setAssigningConsultant: PropTypes.func,
};

AssignConsultant.propTypes = {
  showFindConsultant: PropTypes.bool,
  showCheckout: PropTypes.bool,
  consultantList: PropTypes.array,
  setAssigningConsultant: PropTypes.func,
  consultantRequested: PropTypes.bool,
  setConsultant: PropTypes.func,
  setConsultantRequested: PropTypes.func,
  updateCustomer: PropTypes.func,
  handleUpdateCartAttributes: PropTypes.func,
};

AssignConsultantTab.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  isActive: PropTypes.bool,
  setActiveTab: PropTypes.func,
  children: PropTypes.node,
};

AssignConsultantTabContent.propTypes = {
  children: PropTypes.node,
};

export default Consultant;
